import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Users, Stethoscope, CalendarDays, IndianRupee, TrendingUp, TrendingDown,
  AlertTriangle, BedDouble, Clock, Activity, ChevronRight, X,
  BarChart3, ArrowUpRight, ArrowDownRight, Building2, CreditCard, Shield,
  UserCheck, UserX, CircleAlert, FileText, Eye, Filter,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import TodaysCelebrationsWidget from '../components/TodaysCelebrationsWidget';

const FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
];

// ========== SPARKLINE COMPONENT ==========
const Sparkline = ({ data, color = '#10b981', height = 32 }) => {
  if (!data?.length) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${100 - (v / max) * 80}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ========== KPI CARD ==========
const KpiCard = ({ label, value, change, icon: Icon, iconBg, trend, prefix, suffix, testId }) => {
  const isPositive = change >= 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 group" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shadow-sm", iconBg)}>
          <Icon size={20} className="text-white" />
        </div>
        {change !== undefined && change !== null && (
          <span className={cn(
            "text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5",
            isPositive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
          )}>
            {isPositive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {isPositive ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">
        {prefix}{typeof value === 'number' ? value.toLocaleString('en-IN') : value}{suffix}
      </p>
      {trend?.length > 1 && (
        <div className="mt-2 opacity-60 group-hover:opacity-100 transition-opacity">
          <Sparkline data={trend} color={isPositive ? '#10b981' : '#ef4444'} height={28} />
        </div>
      )}
    </div>
  );
};

// ========== DONUT CHART ==========
const DonutChart = ({ data, size = 140, strokeWidth = 20 }) => {
  const sum = data.reduce((s, d) => s + d.value, 0);
  const total = sum || 1; // for pct math when empty
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const visible = data.filter(d => d.value > 0);
  const single = visible.length === 1;

  return (
    <div className="flex items-center gap-6">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          {sum === 0 && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
          )}
          {sum > 0 && !single && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
          )}
          {single && (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={visible[0].color} strokeWidth={strokeWidth} className="transition-all duration-700" />
          )}
          {!single && visible.map((d, i) => {
            const pct = d.value / total;
            const dash = circumference * pct;
            const gap = circumference - dash;
            const currentOffset = offset;
            offset += pct;
            return (
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
                stroke={d.color} strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-circumference * currentOffset}
                strokeLinecap="butt"
                className="transition-all duration-700" />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-xl font-bold text-slate-800">{sum}</p>
          <p className="text-[10px] uppercase text-slate-400 tracking-wider">Total</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {data.map((d, i) => {
          const pct = sum > 0 ? Math.round((d.value / sum) * 100) : 0;
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
              <span className="text-slate-500">{d.label}</span>
              <span className="font-bold text-slate-700">{d.value}</span>
              {sum > 0 && <span className="text-[10px] text-slate-400">({pct}%)</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ========== BAR CHART ==========
const BarChart = ({ data, height = 160 }) => {
  if (!data?.length) return <p className="text-xs text-slate-400 text-center py-8">No data</p>;
  const max = Math.max(...data.map(d => d.total), 1);
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
            <div className="w-full flex flex-col gap-0.5 justify-end" style={{ height }}>
              <div
                className="w-full bg-emerald-500 rounded-t-sm transition-all duration-500 hover:bg-emerald-400"
                style={{ height: `${(d.completed / max) * 100}%`, minHeight: d.completed > 0 ? 2 : 0 }}
                title={`Completed: ${d.completed}`}
              />
              <div
                className="w-full bg-slate-300 rounded-t-sm transition-all duration-500 hover:bg-slate-400"
                style={{ height: `${((d.total - d.completed) / max) * 100}%`, minHeight: (d.total - d.completed) > 0 ? 2 : 0 }}
                title={`Other: ${d.total - d.completed}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex mt-1">
        {data.map((d, i) => (
          <span key={i} className="flex-1 text-center text-[8px] text-slate-400">{d.month}</span>
        ))}
      </div>
    </div>
  );
};

// ========== PROGRESS RING ==========
const ProgressRing = ({ value, max, label, color, size = 80 }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const dash = (pct / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <circle
          cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeLinecap="round"
          className="transition-all duration-700"
          style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
        />
        <text x="50%" y="50%" textAnchor="middle" dy="0.3em" className="text-sm font-bold fill-slate-700">
          {Math.round(pct)}%
        </text>
      </svg>
      <p className="text-[10px] text-slate-500 font-medium mt-1">{label}</p>
      <p className="text-[10px] text-slate-400">{value}/{max}</p>
    </div>
  );
};

// ========== ALERT BADGE ==========
const AlertBadge = ({ alert }) => {
  const colors = {
    critical: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
  };
  const icons = { critical: AlertTriangle, warning: CircleAlert, info: Activity };
  const Icon = icons[alert.severity] || CircleAlert;
  return (
    <div className={cn("flex items-start gap-3 p-3 rounded-lg border", colors[alert.severity])} data-testid={`alert-${alert.type}`}>
      <Icon size={16} className="flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-semibold">{alert.title}</p>
        <p className="text-[11px] opacity-80 mt-0.5">{alert.message}</p>
      </div>
    </div>
  );
};

// ========== DOCTOR CARD ==========
const DoctorCard = ({ doc, onSelect }) => {
  const statusColors = {
    present: 'bg-emerald-500',
    leave: 'bg-amber-500',
    unavailable: 'bg-slate-400',
  };
  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all duration-200"
      onClick={() => onSelect(doc)}
      data-testid={`doctor-card-${doc.user_id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold">
            {doc.name?.charAt(0) || 'D'}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 leading-tight">{doc.name}</p>
            <p className="text-[10px] text-slate-400">{doc.department}</p>
          </div>
        </div>
        <span className={cn("h-2.5 w-2.5 rounded-full", statusColors[doc.status])} title={doc.status} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-slate-50 rounded-md p-1.5">
          <p className="text-sm font-bold text-slate-700">{doc.today_total}</p>
          <p className="text-[9px] text-slate-400">Total</p>
        </div>
        <div className="bg-emerald-50 rounded-md p-1.5">
          <p className="text-sm font-bold text-emerald-600">{doc.today_checked}</p>
          <p className="text-[9px] text-slate-400">Checked</p>
        </div>
        <div className="bg-amber-50 rounded-md p-1.5">
          <p className="text-sm font-bold text-amber-600">{doc.today_pending}</p>
          <p className="text-[9px] text-slate-400">Pending</p>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN DASHBOARD ==========
export const DashboardPage = () => {
  const { user } = useAuth();
  // Finance data visibility: Hospital Admin, Finance/Accountant, Super Admin ONLY.
  // Front Desk (reception) + Billing Staff are explicitly excluded per product owner.
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const canViewFinance = user?.is_super_admin || ['hospital_admin', 'accountant', 'finance'].includes(userRoleCode);
  const navigate = useNavigate();
  const isSuperAdmin = user?.is_super_admin;
  const hospitalId = localStorage.getItem('selectedHospitalId');
  const showCommandCenter = hospitalId || !isSuperAdmin;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Super admin stats (fallback)
  const [superStats, setSuperStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (showCommandCenter) {
        const params = { filter_type: filter };
        if (filter === 'custom' && customStart && customEnd) {
          params.custom_start = customStart;
          params.custom_end = customEnd;
        }
        const res = await dashboardAPI.commandCenter(params);
        setData(res.data);
      } else {
        const res = await dashboardAPI.getStats();
        setSuperStats(res.data);
      }
    } catch (e) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  }, [filter, customStart, customEnd, showCommandCenter]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 bg-slate-200 rounded w-64 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1,2].map(i => <div key={i} className="h-64 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  // Super admin with no hospital selected -> Platform overview
  if (!showCommandCenter && superStats) {
    return <SuperAdminOverview stats={superStats} />;
  }

  if (!data) return <p className="text-slate-500 text-center py-16">No data available</p>;

  const { kpi, appointment_stats, monthly_appointment_trend, doctor_grid, doctor_summary, finance, availability, alerts, recent_appointments, top_departments, popular_doctors } = data;

  return (
    <div className="space-y-6" data-testid="command-center-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Admin Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {data.period.start === data.period.end ? data.period.start : `${data.period.start} to ${data.period.end}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold rounded-lg transition-all",
                filter === f.value ? "bg-emerald-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
          {filter === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-8 rounded-md border border-slate-300 px-2 text-xs" />
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-8 rounded-md border border-slate-300 px-2 text-xs" />
              <Button size="sm" onClick={load} className="h-8 bg-emerald-600 text-xs">Apply</Button>
            </div>
          )}
        </div>
      </div>

      {/* Operational Alerts */}
      {alerts?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="alerts-section">
          {alerts.filter(a => canViewFinance || !['pending_payments', 'revenue_drop', 'high_expenses'].includes(a.type)).map((a, i) => <AlertBadge key={i} alert={a} />)}
        </div>
      )}

      {/* Today's Celebrations (v37 — only renders when total > 0) */}
      <TodaysCelebrationsWidget />

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 ${canViewFinance ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
        <KpiCard label="Doctors" value={kpi.doctors.value} change={null} icon={Stethoscope} iconBg="bg-indigo-500" testId="kpi-doctors" />
        <KpiCard label="Patients" value={kpi.patients.value} change={kpi.patients.change} icon={Users} iconBg="bg-rose-500" testId="kpi-patients" suffix={kpi.patients.new_in_period > 0 ? ` (+${kpi.patients.new_in_period})` : ''} />
        <KpiCard label="Appointments" value={kpi.appointments.value} change={kpi.appointments.change} icon={CalendarDays} iconBg="bg-blue-500" testId="kpi-appointments" />
        {canViewFinance && (
          <KpiCard label="Revenue" value={kpi.revenue.value} change={kpi.revenue.change} icon={IndianRupee} iconBg="bg-emerald-500" trend={kpi.revenue.trend} prefix="Rs." testId="kpi-revenue" />
        )}
      </div>

      {/* Main Grid: Appointment Stats + Doctor Schedule + Finance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Appointment Statistics */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Appointment Statistics</h3>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { label: 'All', value: appointment_stats.total, color: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
              { label: 'Completed', value: appointment_stats.completed, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
              { label: 'Cancelled', value: appointment_stats.cancelled, color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
              { label: 'Scheduled', value: appointment_stats.scheduled + (appointment_stats.checked_in || 0), color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
            ].map((s, i) => (
              <div key={i} className={cn("rounded-lg border p-3 text-center", s.color)} data-testid={`apt-stat-${s.label.toLowerCase()}`}>
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <span className={cn("h-2 w-2 rounded-full", s.dot)} />
                  <span className="text-[10px] font-medium">{s.label}</span>
                </div>
                <p className="text-xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>
          {/* Monthly trend */}
          <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center gap-1 text-[10px] text-slate-400"><span className="h-2 w-2 bg-emerald-500 rounded-full inline-block" /> Completed</span>
            <span className="flex items-center gap-1 text-[10px] text-slate-400"><span className="h-2 w-2 bg-slate-300 rounded-full inline-block" /> Total</span>
          </div>
          <BarChart data={monthly_appointment_trend} />
        </div>

        {/* Doctor Schedule Summary */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Doctors Schedule</h3>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Available', value: doctor_summary.available, color: 'text-emerald-600' },
              { label: 'Unavailable', value: doctor_summary.unavailable, color: 'text-slate-500' },
              { label: 'Leave', value: doctor_summary.on_leave, color: 'text-amber-600' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className={cn("text-2xl font-bold", s.color)}>{s.value}</p>
                <p className="text-[10px] text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
          {/* Popular doctors */}
          <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Top Doctors</h4>
          <div className="space-y-2">
            {popular_doctors?.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-[10px] font-bold">
                    {d.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-700">{d.name}</p>
                    <p className="text-[9px] text-slate-400">{d.department}</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-slate-600">{d.bookings}</span>
              </div>
            ))}
            {(!popular_doctors || popular_doctors.length === 0) && (
              <p className="text-xs text-slate-400 text-center py-4">No doctor data</p>
            )}
          </div>
        </div>
      </div>

      {/* Finance + Availability Row */}
      <div className={`grid grid-cols-1 ${canViewFinance ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6`}>
        {/* Finance Summary - hidden for IT Admin & non-finance roles */}
        {canViewFinance && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="finance-summary-card">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><IndianRupee size={14} /> Finance Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Revenue</span>
                <span className="text-sm font-bold text-emerald-600">Rs.{finance.revenue.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Expenses</span>
                <span className="text-sm font-bold text-red-500">Rs.{finance.expenses.toLocaleString('en-IN')}</span>
              </div>
              <hr className="border-slate-100" />
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">Net Profit</span>
                <span className={cn("text-sm font-bold", finance.net_profit >= 0 ? "text-emerald-600" : "text-red-600")}>Rs.{finance.net_profit.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Margin</span>
                <span className={cn("text-sm font-bold", finance.margin >= 0 ? "text-emerald-600" : "text-red-600")}>{finance.margin}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Availability */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Activity size={14} /> Availability Today</h3>
          <div className="flex items-center justify-around">
            <ProgressRing value={availability.staff.present} max={availability.staff.total} label="Staff" color="#10b981" />
            <ProgressRing value={availability.beds.occupied} max={availability.beds.total} label="Beds" color="#3b82f6" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-center">
            <div className="bg-emerald-50 rounded-lg p-2">
              <p className="text-[10px] text-emerald-600 font-medium">Staff Present</p>
              <p className="text-sm font-bold text-emerald-700">{availability.staff.present}/{availability.staff.total}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-2">
              <p className="text-[10px] text-blue-600 font-medium">Beds Available</p>
              <p className="text-sm font-bold text-blue-700">{availability.beds.available}/{availability.beds.total}</p>
            </div>
          </div>
        </div>

        {/* Top Departments */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Building2 size={14} /> Top Departments</h3>
          {top_departments?.length > 0 ? (
            <DonutChart
              data={top_departments.map((d, i) => ({
                label: d.name,
                value: d.bookings,
                color: ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6'][i % 5],
              }))}
            />
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">No department data</p>
          )}
        </div>
      </div>

      {/* Item 8 — "Doctor Availability Today" and "All Appointments" sections moved
          exclusively to the Doctor Dashboard. Admin Dashboard stays focused on
          hospital-wide KPIs (revenue, beds, departments). Link to Doctor Dashboard
          remains in the sidebar. */}
    </div>
  );
};

// ========== SUPER ADMIN OVERVIEW (when no hospital selected) ==========
const SuperAdminOverview = ({ stats }) => {
  const cards = [
    { title: 'Total Hospitals', value: stats?.total_hospitals || 0, icon: Building2, iconBg: 'bg-emerald-500' },
    { title: 'Total Users', value: stats?.total_users || 0, icon: Users, iconBg: 'bg-blue-500' },
    { title: 'Active Plans', value: Object.keys(stats?.plan_distribution || {}).length, icon: CreditCard, iconBg: 'bg-purple-500' },
    { title: 'System Status', value: 'Healthy', icon: Shield, iconBg: 'bg-lime-500' },
  ];

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Platform Overview</h1>
        <p className="text-sm text-slate-500 mt-0.5">Select a hospital to view the Command Center</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <KpiCard key={i} label={c.title} value={c.value} icon={c.icon} iconBg={c.iconBg} testId={`sa-card-${i}`} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><CreditCard size={14} /> Subscription Distribution</h3>
          <div className="space-y-3">
            {Object.entries(stats?.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="text-sm text-slate-700">{plan}</span>
                </div>
                <Badge variant="secondary">{count} hospital{count !== 1 ? 's' : ''}</Badge>
              </div>
            ))}
            {Object.keys(stats?.plan_distribution || {}).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No hospitals registered</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Clock size={14} /> Recent Hospitals</h3>
          <div className="space-y-3">
            {(stats?.recent_hospitals || []).slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-slate-700">{h.name}</p>
                  <p className="text-xs text-slate-400">{h.code}</p>
                </div>
                <Badge className={h.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                  {h.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
